import React, { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { getTenantMovePath } from 'shared/routes/tenant';
import { getUnitOptionLabel } from 'features/tenant/helper';
import { Title } from 'shared/components/abstract/interactables';
import { searchBuildings } from 'shared/api/buildings';
import { tenantEntity } from 'shared/entityDetails';
import { searchBuildingUnits } from 'shared/api/units';
import { AsyncSearchableSelect } from 'shared/components/select/SearchableSelects';
import useTenantMoveState from './useTenantMoveState';
import TenantMoveData from './TenantMoveData';
import TenantMoveSummary from './TenantMoveSummary';
import TenantMoveVerification from './TenantMoveVerification';

export default function TenantMover() {
  const history = useHistory();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const tenantId = parseInt(searchParams.get('tenant_id'), 10);
  const unitId = parseInt(searchParams.get('unit_id'), 10);
  const buildingId = parseInt(searchParams.get('building_id'), 10);

  const [state, dispatch] = useTenantMoveState(buildingId, unitId, tenantId);
  const {
    currentBuilding,
    currentUnit,
    loadError,
    moveComplete,
    moveCount,
    newBuilding,
    newUnit,
    tenant,
  } = state;

  const getBuildingLabel = useCallback((b) => {
    if (currentBuilding && currentBuilding.id === b.id) {
      return `${b.name} (current)`;
    }
    return b.name;
  }, [currentBuilding]);

  const getUnitLabel = useCallback((u) => {
    const label = getUnitOptionLabel(u);
    if (currentUnit && currentUnit.id === u.id) {
      return `${label} (current)`;
    }
    return label;
  }, [currentUnit]);

  const onBuildingChange = (b) => {
    dispatch({
      action: 'building',
      payload: b,
    });
    history.push(getTenantMovePath(b && b.id, null, tenantId));
  };

  const onUnitChange = (u) => {
    dispatch({
      action: 'unit',
      payload: u,
    });
    history.push(getTenantMovePath(buildingId, u && u.id, tenantId));
  };

  const onTenantChange = (t) => {
    dispatch({
      action: 'tenant',
      payload: t,
    });
    history.push(getTenantMovePath(buildingId, unitId, t && t.id));
  };

  const searchUnits = useCallback((searchString) => searchBuildingUnits(searchString, '', buildingId), [buildingId]);
  const showForm = loadError === '' && !moveComplete;
  return (
    <>
      <Title title="Move Tenants" />
      <hr />
      <Grid
        container
        rowSpacing={1}
        columnSpacing={{
          xs: 1,
          sm: 2,
          md: 3,
        }}
      >
        <Grid size={{
          xs: 12,
          md: 12,
          lg: 5,
        }}
        >
          <Grid size={12}>
            <AsyncSearchableSelect
              baseSearch="include_archived=true&ob=it_firstname"
              defaultOptions={false}
              getOptionLabel={tenantEntity.getOptionLabel}
              onChange={onTenantChange}
              label="Tenant"
              searchRequest={tenantEntity.searchRequest}
              selectedOption={tenant}
            />
          </Grid>
          <Grid size={12}>
            <AsyncSearchableSelect
              defaultOptions={false}
              onChange={onBuildingChange}
              label="New Building"
              searchRequest={searchBuildings}
              selectedOption={newBuilding}
              getOptionLabel={getBuildingLabel}
            />
          </Grid>
          <Grid size={12}>
            <AsyncSearchableSelect
              updateCount={moveCount}
              onChange={onUnitChange}
              label="New Unit"
              searchRequest={searchUnits}
              getOptionLabel={getUnitLabel}
              disabled={!buildingId}
              selectedOption={newUnit}
            />
          </Grid>
          {showForm && <TenantMoveData tenantMoveState={state} />}
        </Grid>
        {showForm && (
          <Grid size={{
            xs: 12,
            md: 12,
            lg: 5,
          }}
          >
            <TenantMoveVerification
              tenantMoveDispatch={dispatch}
              tenantMoveState={state}
            />
          </Grid>
        )}
        {!!loadError && <strong>{loadError}</strong>}
        {moveComplete && <TenantMoveSummary tenantMoveState={state} />}
      </Grid>
    </>
  );
}
